import React from 'react';
import { valueClaim, BASIS, PLACEHOLDER_DAILY, PLACEHOLDER_REASON } from '../data/claimValue';
import RulingRef from './RulingRef';   // ruling 168: the number behind Why? for a member, [R-nn] for an officer

// WHAT THIS CLAIM IS WORTH - rulings 88, 93 and 94, rendered. Every decision is in
// src\data\claimValue.js (scripts\test-claim-value.mjs, 69 assertions); this file draws it.
//
// THE BANNER IS THE POINT. Ruling 93.3: every figure computed from the placeholder carries the word
// PLACEHOLDER on screen. It is not a footnote, it is the first thing the eye lands on, because the
// number underneath it looks exactly like a real one and is not (PD-4). Ruling 94: a sourced rate
// older than the newest national round is SHOWN, dated, and NOT multiplied - so the reader sees what
// the platform holds and why it is not using it.
//
// RENDERS NOTHING when the claim has no railroad the platform holds an agreement for: a refusal is
// information for the officer desk, not a banner on a member's claim (ISS-22 - refuse, don't estimate).

const C = {
  card: '#0D1B2A', text: '#D8E2EE', line: '#8AA4BF', head: '#E8A020',
  warnBg: '#2D1F0A', warnBorder: '#E8A020', warnText: '#FFD27A',
  okBorder: '#2E6B4A', ok: '#40C070',
};

const money = (n) => (n == null ? '' : '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

function reasonText(v) {
  switch (v.reason) {
    case PLACEHOLDER_REASON.NO_SOURCED_RATE:
      return 'No wage schedule for ' + v.railroad + ' is on file.';
    case PLACEHOLDER_REASON.SOURCED_RATE_STALE:
      return 'The last sourced base on file for ' + v.railroad + ' is ' + money(v.lastSourced.baseHourly) + '/hr effective ' +
        v.lastSourced.effectiveDate + (v.lastSourced.scope ? ' (' + v.lastSourced.scope + ')' : '') +
        '. National wage rounds since then are not in the record, so it is shown here and not used.';
    case PLACEHOLDER_REASON.NO_RATE_FOR_POSITION:
      return 'No sourced rate is on file for this position and scope as of the claim date.';
    default:
      return '';
  }
}

export default function ClaimWorthPanel({ claim, claimDays, user }) {   // user: ruling 168 (who is looking decides how the number shows)
  const v = valueClaim(claim, { claimDays });
  if (v.basis === BASIS.REFUSED) return null;

  const placeholder = v.basis === BASIS.PLACEHOLDER;
  return (
    <div className="card" style={{ background: C.card, borderLeft: `4px solid ${placeholder ? C.warnBorder : C.okBorder}`, marginBottom: 16 }}>
      <div className="card-header">
        <div>
          <div className="card-title">What this claim is worth</div>
          <div className="card-subtitle" style={{ color: C.head, fontSize: 11, fontWeight: 600 }}>
            {v.claimDays} claim-day{v.claimDays === 1 ? '' : 's'} × 8 hours × time and one-half × the rate of the contested position
            {v.claimDaysStated === false ? <span style={{ color: C.warnText }}> — claim-days NOT RECORDED, assumed as one</span> : null}
          </div>
        </div>
      </div>

      {placeholder && (
        <div style={{
          margin: '8px 0 12px', padding: '8px 12px', borderRadius: 6,
          background: C.warnBg, border: `1px solid ${C.warnBorder}`,
          fontSize: 12.5, color: C.warnText, fontWeight: 700,
        }}>
          PLACEHOLDER RATE — ${PLACEHOLDER_DAILY}/day, not a contract rate.
          <div style={{ fontWeight: 400, marginTop: 4, color: C.text }}>{reasonText(v)}{v.reason === PLACEHOLDER_REASON.SOURCED_RATE_STALE ? <RulingRef user={user} n={94} why="A rate the record cannot source to a current wage round is shown, never used in the amount." /> : null}</div>
          <div style={{ fontWeight: 400, marginTop: 4, color: C.line, fontSize: 11.5 }}>
            This figure may not be presented to a carrier. It is replaced the day the sourced schedule for this unit is on file.
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 24px' }}>
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 10, color: C.line, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 2 }}>Straight-time rate</div>
          <div style={{ fontSize: 13, color: C.text }}>{money(v.hourly)}/hr {placeholder ? <span style={{ color: C.warnText, fontWeight: 700 }}>PLACEHOLDER</span> : <span style={{ color: C.ok }}>sourced</span>}</div>
        </div>
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 10, color: C.line, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 2 }}>Per claim-day (8 × 1.5)</div>
          <div style={{ fontSize: 13, color: C.text }}>{money(v.perClaimDay)}</div>
        </div>
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 10, color: C.line, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 2 }}>Total{placeholder ? ' (PLACEHOLDER)' : ''}{v.claimDaysStated === false ? ' FOR ONE DAY' : ''}</div>
          <div style={{ fontSize: 15, color: C.head, fontWeight: 700 }}>{money(v.total)}</div>
        </div>
      </div>

      {v.claimDaysStated === false && (
        <div style={{ marginTop: 8, padding: '8px 12px', borderRadius: 6, background: C.warnBg,
                      border: '1px solid ' + C.warnBorder, fontSize: 12, color: C.warnText, fontWeight: 600 }}>
          THIS TOTAL IS FOR ONE CLAIM-DAY.
          <div style={{ fontWeight: 400, marginTop: 4, color: C.text }}>
            Nothing on this claim records how many days it covers. If the violation continued, the
            amount is larger than the figure above — on some properties materially so. The platform
            does not guess the span, because what counts as a claim-day and how far back money runs
            are agreement questions, not arithmetic.
          </div>
        </div>
      )}

      {!placeholder && v.rate && (
        <div style={{ marginTop: 6, fontSize: 10.5, color: C.line, fontStyle: 'italic' }}>
          Source: {v.rate.sourceDoc}, effective {v.rate.effectiveDate}.
        </div>
      )}
      <div style={{ marginTop: 6, fontSize: 10.5, color: C.line, fontStyle: 'italic' }}>
        8 hours at time and one half at the rate of the position contested. This is information for the member and his representatives; the amount claimed on the claim itself is entered by the filer.<RulingRef user={user} n={88} why="What a claim is worth is figured at 8 hours at time and one half at the rate of the position contested." />
      </div>
    </div>
  );
}
